import React, { useState } from 'react';
import { Eye, EyeOff, ArrowRight, CheckCircle } from 'lucide-react';
import OTPModal from '../HrDashboard/utils/OTPmodal';
import SignupHeader from './SignUp/SignUpHeader';
import SignupForm from './SignUp/SignUpForm';
import SignupFeatures from './SignUp/SignUpFeatures';
import { signUpUser } from '../services/Queries/Auths';

const SignupPage = () => {
  const [step, setStep] = useState(1); // Step 1: Basic, Step 2: Details, Step 3: Success
  const [showOTPModal, setShowOTPModal] = useState(false);
  const [otpLoading, setOtpLoading] = useState(false);
  const [otpError, setOtpError] = useState(null);
  const [emailVerified, setEmailVerified] = useState(false);

  const [formData, setFormData] = useState({
    // Step 1: Basic Information
    firstName: '',
    lastName: '',
    email: '',
    password: '',
    confirmPassword: '',

    // Step 2: Additional Details
    phone: '',
    userType: 'user', // 'caregiver' | 'patient' | 'family'
    experience: '', // Years of experience (for caregivers)
    address: '',
    city: '',
    state: 'MN',
    zipCode: '',
    agreeTerms: false,
    newsletter: false,
  });

  const [errors, setErrors] = useState({});
  const [isLoading, setIsLoading] = useState(false);

  // Step 1 Validation
  const validateStep1 = () => {
    const newErrors = {};

    if (!formData.firstName.trim()) {
      newErrors.firstName = 'First name is required';
    }
    if (!formData.lastName.trim()) {
      newErrors.lastName = 'Last name is required';
    }
    if (!formData.email.trim()) {
      newErrors.email = 'Email is required';
    } else if (!/^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(formData.email)) {
      newErrors.email = 'Please enter a valid email';
    }
    if (!formData.password) {
      newErrors.password = 'Password is required';
    } else if (formData.password.length < 8) {
      newErrors.password = 'Password must be at least 8 characters';
    } else if (!/(?=.*[a-z])(?=.*[A-Z])(?=.*\d)/.test(formData.password)) {
      newErrors.password = 'Password must contain uppercase, lowercase, and numbers';
    }
    if (!formData.confirmPassword) {
      newErrors.confirmPassword = 'Please confirm your password';
    } else if (formData.password !== formData.confirmPassword) {
      newErrors.confirmPassword = 'Passwords do not match';
    }

    setErrors(newErrors);
    return Object.keys(newErrors).length === 0;
  };

  // Step 2 Validation
  const validateStep2 = () => {
    const newErrors = {};

    if (!formData.phone.trim()) {
      newErrors.phone = 'Phone number is required';
    } else if (!/^[\d\-\s()]{10,}$/.test(formData.phone.replace(/\D/g, ''))) {
      newErrors.phone = 'Please enter a valid phone number';
    }
    if (!formData.userType) {
      newErrors.userType = 'Please select your role';
    }
    if (!formData.address.trim()) {
      newErrors.address = 'Address is required';
    }
    if (!formData.city.trim()) {
      newErrors.city = 'City is required';
    }
    if (!formData.zipCode.trim()) {
      newErrors.zipCode = 'Zip code is required';
    }
    if (!formData.agreeTerms) {
      newErrors.agreeTerms = 'You must agree to the terms and conditions';
    }

    setErrors(newErrors);
    return Object.keys(newErrors).length === 0;
  };

  const handleInputChange = (e) => {
    const { name, value, type, checked } = e.target;
    setFormData((prev) => ({
      ...prev,
      [name]: type === 'checkbox' ? checked : value,
    }));
    // Clear error for this field
    if (errors[name]) {
      setErrors((prev) => ({
        ...prev,
        [name]: '',
      }));
    }
  };

  const handleNextStep = async () => {
    console.log("Current Step: ", step);
    if (step === 1) {
      if (validateStep1()) {
        // Open OTP modal for email verification
        setShowOTPModal(true);
        setOtpError(null);
      }
    } else if (step === 2) {
      if (validateStep2()) {
        // Submit form
        handleSubmit(formData);
      }
    }
  };

  // Handle OTP verification
  const handleOTPVerify = async (otpCode) => {
    setOtpLoading(true);
    setOtpError(null);

    try {
     
      await new Promise((resolve) => setTimeout(resolve, 1500));

      // Mock verification - accept any 6 digit code
      if (otpCode.length === 6) {
        setEmailVerified(true);
        setShowOTPModal(false);
        setStep(2); // Move to step 2
        setOtpLoading(false);
        return true;
      } else {
        setOtpError('Invalid OTP format');
        setOtpLoading(false);
        return false;
      }
    } catch (error) {
      setOtpError('Verification failed. Please try again.');
      setOtpLoading(false);
      return false;
    }
  };

  const handleSubmit = async (formData) => {
    setIsLoading(true);
    console.log('Form Data: ' , formData)
    try {
    const result = await signUpUser(formData);
    console.log('Results: ', result);
    
    if (result?.authData?.user) {
      setStep(3); // Success screen
    } else {
      throw new Error('User creation failed');
    }
  } catch (error) {
    console.error('Error Message: ', error);
    alert(error.message || 'An error occurred during sign up');
  } finally {
    setIsLoading(false);
  }

  };

  const handleBackStep = () => {
    if (step > 1) {
      setStep(step - 1);
      setErrors({});
    }
  };

  return (
    <div className="min-h-screen flex items-center justify-center p-4" style={{ background: 'linear-gradient(135deg, #0095fa 0%, #0f6caa 100%)' }}>
      {/* Decorative elements */}
      <div className="fixed top-0 left-0 w-full h-full pointer-events-none overflow-hidden">
        {/* Top right accent */}
        <div className="absolute -top-40 -right-40 w-96 h-96 rounded-full" style={{ backgroundColor: '#ffffff', opacity: 0.1 }} />
        {/* Bottom left accent */}
        <div className="absolute -bottom-40 -left-40 w-96 h-96 rounded-full" style={{ backgroundColor: '#ffffff', opacity: 0.08 }} />
      </div>

      {/* OTP Modal */}
      <OTPModal
        isOpen={showOTPModal}
        email={formData.email}
        onClose={() => setShowOTPModal(false)}
        onVerify={handleOTPVerify}
        isLoading={otpLoading}
        error={otpError}
      />

      {/* Main container */}
      <div className="relative z-10 w-full max-w-5xl">
        <div className="grid grid-cols-1 lg:grid-cols-2 gap-8">
          {/* Left side - Signup Form */}
          <div className="flex flex-col justify-center bg-white rounded-2xl p-8 md:p-10 shadow-2xl" style={{ boxShadow: '0 20px 60px rgba(0, 0, 0, 0.15)' }}>
            <SignupHeader step={step} emailVerified={emailVerified} />

            {step === 3 ? (
              // Success Screen
              <div className="text-center space-y-4 py-8">
                <div className="flex justify-center">
                  <div className="w-16 h-16 rounded-full flex items-center justify-center" style={{ backgroundColor: '#10b981' }}>
                    <CheckCircle size={32} className="text-white" />
                  </div>
                </div>
                <h2 className="text-2xl font-bold text-gray-800" style={{ fontFamily: 'Montserrat' }}>
                  Welcome to Samah Home Care!
                </h2>
                <p className="text-gray-600">
                  Your account has been created successfully. We're sending a confirmation email to <span className="font-semibold">{formData.email}</span>
                </p>
                <div className="pt-4 space-y-3">
                  <p className="text-sm text-gray-500">Next steps:</p>
                  <ul className="text-left space-y-2 text-sm text-gray-600">
                    <li>✓ Email verified</li>
                    <li>✓ Account created</li>
                    <li>✓ Complete your profile</li>
                    <li>✓ Start providing/receiving care</li>
                  </ul>
                </div>
                <a
                  href="/login"
                  className="inline-block mt-6 px-6 py-3 rounded-lg font-semibold text-white transition-all duration-300"
                  style={{
                    backgroundColor: '#0095fa',
                    boxShadow: '0 5px 15px rgba(0, 149, 250, 0.4)',
                  }}
                  onMouseEnter={(e) => {
                    e.target.style.backgroundColor = '#0f6caa';
                    e.target.style.boxShadow = '0px 0px 20px rgba(0, 149, 250, 0.8)';
                  }}
                  onMouseLeave={(e) => {
                    e.target.style.backgroundColor = '#0095fa';
                    e.target.style.boxShadow = '0 5px 15px rgba(0, 149, 250, 0.4)';
                  }}
                >
                  Go to Login
                </a>
              </div>
            ) : (
              <SignupForm
                step={step}
                formData={formData}
                errors={errors}
                onInputChange={handleInputChange}
                onNextStep={handleNextStep}
                onBackStep={handleBackStep}
                isLoading={isLoading}
                emailVerified={emailVerified}
              />
            )}
          </div>

          {/* Right side - Features/Benefits */}
          <div className="hidden lg:flex flex-col justify-center space-y-6">
            <SignupFeatures />
          </div>
        </div>

        {/* Mobile features - show on small screens */}
        <div className="lg:hidden mt-8">
          <SignupFeatures />
        </div>
      </div>
    </div>
  );
};

export default SignupPage;